import React from 'react';

const fmt = (n) => {
    const v = Number(n) || 0;
    const s = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Math.abs(v));
    return v < 0 ? `-${s}` : s;
};
const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const dayLabel = (ymd) => {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-').map(Number);
    return `${DAYS[new Date(y, m - 1, d).getDay()]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
};
const isCb = (concept) => (concept || '').toLowerCase().startsWith('chargeback');
const bonusLabel = (b) => b.description || (b.type === 'objective' ? 'Bono por objetivo' : b.type === 'production' ? 'Bono por producción' : 'Bono');

export const receiptTotals = (c) => {
    const ded = c.deductions || [];
    const sum = (arr) => Math.round(arr.reduce((s, d) => s + (Number(d.amount) || 0), 0) * 100) / 100;
    return {
        base: Number(c.commission_amount) || 0,
        bonus: Number(c.bonus_total) || 0,
        chargebacks: sum(ded.filter((d) => isCb(d.concept))),
        other: sum(ded.filter((d) => !isCb(d.concept))),
        net: Number(c.net_amount) || 0,
    };
};

export const groupDays = (c) => {
    const map = {};
    (c.sales_detail || []).forEach((s) => {
        const d = (map[s.date] ||= { date: s.date, folios: [], commission: 0, sales: [], bonuses: [] });
        d.folios.push(...(s.folios || []));
        d.commission = Math.round((d.commission + (Number(s.commission_amount) || 0)) * 100) / 100;
        d.sales.push(s);
    });
    (c.bonuses || []).filter((b) => b.date).forEach((b) => {
        (map[b.date] ||= { date: b.date, folios: [], commission: 0, sales: [], bonuses: [] }).bonuses.push(b);
    });
    return Object.keys(map).sort().map((k) => map[k]);
};

export const ReceiptDayBoxes = ({ commission }) => {
    const days = groupDays(commission);
    if (!commission.sales_detail?.length) {
        return (
            <p className="mb-5 p-3 rounded-md bg-[#F8F4EC] text-xs text-[#4B5878]" data-testid="legacy-receipt-note">
                Recibo anterior al nuevo formato: no tiene líneas por venta; se muestra solo el resumen guardado.
            </p>
        );
    }
    return (
        <div className="mb-5 space-y-2" data-testid="receipt-day-boxes">
            <p className="text-[10px] uppercase tracking-wider text-[#8B8474]">Comisión por día</p>
            {days.map((d) => (
                <div key={d.date} className="grid grid-cols-[1fr_auto] sm:grid-cols-[110px_1fr_120px] border border-[#D9CDB4] rounded-md overflow-hidden" data-testid={`receipt-day-${d.date}`}>
                    <div className="bg-[#F8F4EC] px-3 py-2 text-xs font-bold text-[#1F2D4B] col-span-2 sm:col-span-1">{dayLabel(d.date)}</div>
                    <div className="px-3 py-2">
                        <p className="font-mono text-[10px] text-[#4B5878] leading-relaxed">{d.folios.join(', ') || '—'}</p>
                        {d.bonuses.map((b, i) => (
                            <p key={i} className="text-[11px] font-semibold text-[#2D7A52] mt-1">{bonusLabel(b)}: +{fmt(b.amount)}</p>
                        ))}
                    </div>
                    <div className="px-3 py-2 text-right">
                        <p className="font-mono font-bold text-[#1F2D4B]" data-testid={`receipt-day-total-${d.date}`}>{fmt(d.commission)}</p>
                        {d.bonuses.length > 0 && (
                            <p className="text-[10px] text-[#2D7A52]">+ bono {fmt(d.bonuses.reduce((s, b) => s + (Number(b.amount) || 0), 0))}</p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const ReceiptTotals = ({ commission }) => {
    const t = receiptTotals(commission);
    const Row = ({ label, value, className = '', testid }) => (
        <div className={`flex justify-between text-sm ${className}`}>
            <span>{label}</span>
            <span className="font-mono font-semibold" data-testid={testid}>{fmt(value)}</span>
        </div>
    );
    return (
        <div className="space-y-1.5" data-testid="receipt-totals">
            <Row label="Comisión base del periodo" value={t.base} testid="receipt-total-base" />
            <Row label="Bonos del periodo" value={t.bonus} className="text-[#2D7A52]" testid="receipt-total-bonus" />
            {t.chargebacks > 0 && <Row label="Contracargos" value={-t.chargebacks} className="text-[#C8413A]" testid="receipt-total-chargebacks" />}
            {t.other > 0 && <Row label="Otras deducciones" value={-t.other} className="text-[#C8413A]" testid="receipt-total-other" />}
            <div className="flex justify-between pt-3 border-t-2 border-[#1F2D4B]">
                <span className="font-display font-bold text-lg">TOTAL A PAGAR</span>
                <span className="font-display font-bold text-xl" style={{ color: t.net < 0 ? '#C8413A' : '#65C76A' }} data-testid="receipt-total-net">{fmt(t.net)}</span>
            </div>
        </div>
    );
};

export const SalesDetailTable = ({ commission }) => {
    const days = groupDays(commission).filter((d) => d.sales.length);
    if (!days.length) return null;
    const pct = commission.commission_percent;
    const cbs = commission.chargebacks_detail
        || (commission.deductions || []).filter((d) => isCb(d.concept)).map((d) => ({ concept: d.concept, commission_amount: d.amount, folios: [] }));
    const t = receiptTotals(commission);
    return (
        <details className="mb-5 border border-[#ECE3D2] rounded-md" data-testid="commission-sales-detail">
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-[#1F2D4B] bg-[#F8F4EC]">
                Detalle de ventas ({commission.sales_detail.length} ventas) — revisar antes de firmar
            </summary>
            <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[560px]">
                <thead className="bg-[#1F2D4B] text-white">
                    <tr>
                        <th className="text-left px-3 py-2">Fecha / hora</th>
                        <th className="text-left px-3 py-2">Folio</th>
                        <th className="text-left px-3 py-2">Producto</th>
                        <th className="text-right px-3 py-2">Monto</th>
                        <th className="text-center px-3 py-2">%</th>
                        <th className="text-right px-3 py-2">Comisión</th>
                    </tr>
                </thead>
                <tbody>
                    {days.map((d) => (
                        <React.Fragment key={d.date}>
                            <tr className="bg-[#F8F4EC]"><td colSpan={6} className="px-3 py-1.5 font-bold text-[#1F2D4B]">{dayLabel(d.date)}</td></tr>
                            {d.sales.map((s) => (
                                <tr key={s.sale_id} className="border-t border-[#ECE3D2]" data-testid={`commission-sale-row-${s.sale_id}`}>
                                    <td className="px-3 py-1.5 whitespace-nowrap">{dayLabel(s.date)} {s.time}</td>
                                    <td className="px-3 py-1.5 font-mono text-[10px]">{(s.folios || []).join(', ')}</td>
                                    <td className="px-3 py-1.5">{s.products || '—'}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{fmt(s.amount)}</td>
                                    <td className="px-3 py-1.5 text-center">{s.commission_percent ?? pct}%</td>
                                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmt(s.commission_amount)}</td>
                                </tr>
                            ))}
                            <tr className="border-t border-[#1F2D4B] font-bold text-[#1F2D4B]">
                                <td className="px-3 py-1.5" colSpan={3}>Subtotal {dayLabel(d.date)} · {d.sales.length} venta(s)</td>
                                <td className="px-3 py-1.5 text-right font-mono">{fmt(d.sales.reduce((a, s) => a + (Number(s.amount) || 0), 0))}</td>
                                <td />
                                <td className="px-3 py-1.5 text-right font-mono">{fmt(d.commission)}</td>
                            </tr>
                        </React.Fragment>
                    ))}
                    {cbs.map((cb, i) => (
                        <tr key={`cb-${i}`} className="border-t border-[#ECE3D2] text-[#C8413A]" data-testid={`commission-chargeback-row-${i}`}>
                            <td className="px-3 py-1.5">{cb.date ? dayLabel(cb.date) : ''}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{(cb.folios || []).join(', ')}</td>
                            <td className="px-3 py-1.5">{cb.concept || 'Contracargo'}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{cb.sale_amount ? fmt(-cb.sale_amount) : ''}</td>
                            <td className="px-3 py-1.5 text-center">{cb.commission_percent ? `${cb.commission_percent}%` : ''}</td>
                            <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmt(-cb.commission_amount)}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-[#1F2D4B] bg-[#F8F4EC] font-bold">
                        <td className="px-3 py-2" colSpan={5}>TOTAL A PAGAR (igual al recibo)</td>
                        <td className="px-3 py-2 text-right font-mono" data-testid="detail-total-net">{fmt(t.net)}</td>
                    </tr>
                </tbody>
            </table>
            </div>
        </details>
    );
};
